import SERVICES_API_V1 from 'api/services/v1'
import SERVICES_API_V2 from 'api/services/v2'
import { call, debounce, put, select, takeLatest } from 'redux-saga/effects'
import { cancellableCall } from 'store/saga-utils/cancellableCall.saga'
import { confirmSaga } from 'store/saga-utils/confirm.saga'
import i18n from 'i18next'
import * as Sentry from '@sentry/browser'
import { notificationShow } from 'modules/Notifications/actions'
import {
  ACCOUNT_ARCHIVE,
  ACCOUNT_DELETE,
  ACCOUNT_QUERY_TYPE,
  ACCOUNT_TRANSFERS_CLEAN,
  FETCH_ACCOUNT_TRANSFERS,
  FETCH_NETWORK_ACCOUNT,
  GET_ACCOUNT_EXTRA_DATA,
  REMOVE_AND_FETCH_NETWORK_ACCOUNTS,
  SET_ACCOUNT_DATA,
  SET_NETWORK_ACCOUNT_QUERY,
  SET_TRANSFERS_DATA,
} from 'modules/Services/types'

function* fetchNetworkAccount({ payload, meta }) {
  try {
    yield put({
      type: SET_ACCOUNT_DATA,
      payload: {
        loading: true,
        loaded: false,
        failed: false,
      },
    })

    const { accountId } = payload

    const { response, error, canceled } = yield cancellableCall(
      SERVICES_API_V2.getNetworkAccountById,
      { accountId },
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    yield put({
      type: SET_ACCOUNT_DATA,
      payload: {
        ...response.data,
        loaded: true,
        timestamp: Date.now(),
      },
    })
    meta?.resolve(response)
  } catch (error) {
    yield put({
      type: SET_ACCOUNT_DATA,
      payload: {
        loaded: true,
        failed: true,
      },
    })
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    meta?.reject(error)

    Sentry.captureMessage(
      `API error ${error.status} when getting account: ${payload.accountId}`,
    )

    console.logXHR(error)
  } finally {
    yield put({
      type: SET_ACCOUNT_DATA,
      payload: {
        loading: false,
      },
    })
  }
}

function* fetchAccountTransfers({ payload }) {
  try {
    const { transfers } = yield select(
      (state) => state['services/network-account'],
    )

    yield put({
      type: SET_ACCOUNT_DATA,
      payload: {
        transfers: {
          ...transfers,
          loading: true,
        },
      },
    })

    const { response, error, canceled } = yield cancellableCall(
      SERVICES_API_V1.getAccountTransfers,
      {
        ...payload,
      },
      arguments,
    )

    if (canceled) return

    if (error) {
      throw error
    }

    yield put({
      type: SET_TRANSFERS_DATA,
      payload: {
        loading: false,
        loaded: true,
        data: transfers.data.concat(response.data.transfers),
        pagination: response.data.pagination,
        summary: response.data.summary,
      },
    })
  } catch (error) {
    yield put({ type: ACCOUNT_TRANSFERS_CLEAN })
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  }
}

function* accountDelete({
  payload,
  meta: { resolve = () => {}, reject = () => {} } = {},
}) {
  try {
    const { login } = yield select((state) => state['services/network-account'])

    const [confirmed, UUID] = yield call(confirmSaga, {
      modalType: 'confirmation',
      ...payload,
    })

    if (!confirmed) {
      yield put({ type: 'MODAL/HIDE', payload: { UUID } })
      reject()
    } else {
      const { response, error } = yield call(SERVICES_API_V2.deleteAccount, {
        data: {
          ids: payload.ids,
        },
      })

      if (error) {
        throw error
      }

      const status = response.data[0].status === 'success' ? 'success' : 'error'

      yield put({ type: 'MODAL/HIDE', payload: { UUID } })
      yield put(
        notificationShow({
          type: status,
          message: i18n.t(
            `services::account_list.group_notifications.delete.${status}`,
            {
              login,
            },
          ),
        }),
      )

      resolve()
    }
  } catch (e) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_update_failed',
      }),
    )

    reject(e)
  }
}

function* accountArchive({
  payload,
  meta: { resolve = () => {}, reject = () => {} } = {},
}) {
  try {
    const { login } = yield select((state) => state['services/network-account'])

    const { response, error } = yield call(
      SERVICES_API_V2.archiveNetworkAccountsByIDs,
      {
        data: {
          ids: payload.ids,
        },
      },
    )

    if (error) {
      throw error
    }

    const status = response.data[0].status === 'success' ? 'success' : 'error'

    yield put(
      notificationShow({
        type: status,
        message: i18n.t(
          `services::account_list.group_notifications.archive.${status}`,
          {
            login,
          },
        ),
      }),
    )

    yield put({
      type: REMOVE_AND_FETCH_NETWORK_ACCOUNTS,
      payload: {
        data: response.data,
      },
    })

    resolve()
  } catch (e) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_update_failed',
      }),
    )

    reject(e)
  }
}

function* typeQuery({ payload }) {
  yield put({ type: SET_NETWORK_ACCOUNT_QUERY, payload })
}

function* fetchNetworkAccountExtraData({ payload }) {
  try {
    const { response, error } = yield call(
      SERVICES_API_V2.getNetworkAccountExtraData,
      { ...payload },
    )

    if (error) {
      throw error
    }

    yield put({
      type: SET_ACCOUNT_DATA,
      payload: {
        extra_data: {
          ...response.data,
        },
      },
    })
  } catch (e) {
    console.log(e)
  }
}

export default function* Sagas() {
  yield takeLatest(FETCH_NETWORK_ACCOUNT, fetchNetworkAccount)
  yield takeLatest(FETCH_ACCOUNT_TRANSFERS, fetchAccountTransfers)
  yield takeLatest(GET_ACCOUNT_EXTRA_DATA, fetchNetworkAccountExtraData)
  yield takeLatest(ACCOUNT_DELETE, accountDelete)
  yield takeLatest(ACCOUNT_ARCHIVE, accountArchive)
  yield debounce(500, ACCOUNT_QUERY_TYPE, typeQuery)
}
